// SF-147 Component 1/2: extracted from the retired WarRoom.tsx — the reducer/derived-state
// "brain" both MobileWarRoomShell/DesktopWarRoomShell render from. Called exactly once, in
// Attack.tsx (Component 1, Phase 3 Decision 7) — a viewport breakpoint flip unmounts one shell and
// mounts the other, so calling this hook per-shell would silently discard the player's
// in-progress army composition on every resize crossing the mobile/desktop threshold.
//
// SF-174: reducer redesign — `dirtyFields`/`seedFrom`'s implicit auto/manual tracking replaced by
// an explicit `mode: 'auto' | 'manual'` field. See Component 5 in the spec for the full reasoning.

import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { RACE_UNIT_STATS } from '../constants/game';
import { calculateTravelTime } from '../constants/attackCalculator';
import { isProtected } from '../utils/protection';
import { isIntelStale } from '../utils/intelStaleness';
import { validateArmyComposition } from '../utils/attackValidation';
import { launchAttack } from '../api/attack';
import {
  computeMinimumWinningForce,
  computeModifiedOffense,
  type UnitPool,
  type AttackMods,
  type ArmyComposition as MwfComposition,
} from '../utils/minimumWinningForce';
import type { Outcome } from '../components/attack/WarRoomOutcome';
import type { Province } from '../types/province';
import type { ArmyComposition, AttackResult, AttackType } from '../types/attack';
import type { TravelTimeBreakdown } from '../types/attackCalculator';
import type { KingdomMember, Kingdom as KingdomInfo } from '../types/magic';
import type { TargetIntelData } from './useTargetIntel';

type FieldKey = 'offenseSpecialists' | 'elites' | 'warHorses' | 'prisoners' | 'soldiers' | 'mercenaries';

interface WarRoomState {
  mode: 'auto' | 'manual';
  generals: number;
  oversendPct: number;
  offenseSpecialists: number;
  elites: number;
  warHorses: number;
  prisoners: number;
  soldiers: number;
  mercenaries: number;
  includeSoldiers: boolean;
  includeMercenaries: boolean;
  autoSwitchReason: 'impossible' | null;
}

type WarRoomAction =
  | { type: 'GENERALS_CHANGED'; generals: number }
  | { type: 'OVERSEND_CHANGED'; pct: number }
  | { type: 'FIELD_CHANGED'; field: FieldKey; value: number }
  | { type: 'TOGGLE_INCLUDE_SOLDIERS'; on: boolean }
  | { type: 'TOGGLE_INCLUDE_MERCS'; on: boolean }
  | { type: 'SWITCH_TO_MANUAL'; seedFrom: MwfComposition | null }
  | { type: 'SWITCH_TO_AUTO' }
  | { type: 'SEND_EVERYTHING'; composition: MwfComposition }
  | { type: 'AUTO_SWITCH_IMPOSSIBLE'; composition: MwfComposition }
  | { type: 'RESET_FOR_NEW_TARGET'; composition: MwfComposition };

function seedComposition(state: WarRoomState, c: MwfComposition): WarRoomState {
  return {
    ...state,
    offenseSpecialists: c.offenseSpecialists,
    elites: c.elites,
    warHorses: c.warHorses,
    prisoners: c.prisoners,
    soldiers: c.soldiers,
    mercenaries: c.mercenaries,
    includeSoldiers: c.soldiers > 0,
    includeMercenaries: c.mercenaries > 0,
  };
}

function reducer(state: WarRoomState, action: WarRoomAction): WarRoomState {
  switch (action.type) {
    case 'GENERALS_CHANGED':
      // Does NOT touch mode — a Generals edit while in Manual doesn't revert to Auto or discard
      // the hand-built composition; the freshly recomputed mwfResult just sits unused until the
      // player's next explicit SWITCH_TO_MANUAL reseeds from it.
      return { ...state, generals: action.generals };
    case 'OVERSEND_CHANGED':
      // Oversend % renders Auto-only, so this is UI-unreachable in Manual — no mode interaction.
      return { ...state, oversendPct: action.pct };
    case 'FIELD_CHANGED': {
      // Manual only. For soldiers/mercenaries, also derives the corresponding include flag from
      // value > 0 — "typing a count is the opt-in," fixing the collision between Manual's "no
      // separate Include gate" design and validateArmyComposition's launch guard.
      const next: WarRoomState = { ...state, [action.field]: action.value };
      if (action.field === 'soldiers') next.includeSoldiers = action.value > 0;
      if (action.field === 'mercenaries') next.includeMercenaries = action.value > 0;
      return next;
    }
    case 'TOGGLE_INCLUDE_SOLDIERS':
      return { ...state, includeSoldiers: action.on, soldiers: 0 };
    case 'TOGGLE_INCLUDE_MERCS':
      return { ...state, includeMercenaries: action.on, mercenaries: 0 };
    case 'SWITCH_TO_MANUAL': {
      const base: WarRoomState = { ...state, mode: 'manual', autoSwitchReason: null };
      return action.seedFrom ? seedComposition(base, action.seedFrom) : base;
    }
    case 'SWITCH_TO_AUTO':
      // Retires the standalone "Reset to MWF" button — the Auto tab now serves that purpose.
      // Also resets includeSoldiers/includeMercenaries to false: without this, a value Manual set
      // to true would silently persist into Auto's solver pool the next time Auto's own inline
      // Include toggle is read, widening the MWF solver's greedy fill with no corresponding UI
      // state showing it's checked.
      return { ...state, mode: 'auto', autoSwitchReason: null, includeSoldiers: false, includeMercenaries: false };
    case 'SEND_EVERYTHING':
      // Deliberate, player-initiated action (the chip-play button) — always forces Manual
      // regardless of the tab active when pressed, otherwise Auto would immediately recompute
      // over this composition. Doesn't touch autoSwitchReason/triggers no banner — the button
      // stays reachable and idempotent even after an auto-switch.
      return seedComposition({ ...state, mode: 'manual' }, action.composition);
    case 'AUTO_SWITCH_IMPOSSIBLE':
      return seedComposition(
        { ...state, mode: 'manual', autoSwitchReason: 'impossible' },
        action.composition
      );
    case 'RESET_FOR_NEW_TARGET':
      // A Manual army or "can't be beaten" banner built for the previous target must not carry
      // over onto a newly-selected one — reseeds the raw composition fields (not just mode) back
      // to the full-army default, matching buildInitialState. Without this, effectiveComposition's
      // null-mwfResult fallback (the state right after a switch, before fresh SOHD loads) would
      // keep displaying — and Launch would actually send — the previous target's stale Manual army.
      return seedComposition(
        { ...state, mode: 'auto', autoSwitchReason: null },
        action.composition
      );
    default:
      return state;
  }
}

/**
 * Exported for reuse by AmbushLaunchPanel — same unit-pool derivation, distinct launch flow.
 *
 * SF-183 Component 7: sources the four Army-eligible troop types from the new at-home-only DTO
 * fields instead of the away-inclusive `population.*` ones — the picker can no longer offer units
 * already committed to another attack. Fallback contract: a missing `...AtHome` field (pre-deploy
 * Redis-cached DTO) degrades to the away-inclusive sibling, never `?? 0` — zeroing the pool would
 * block launches outright during the cache window, a worse regression than staying briefly
 * away-inclusive.
 */
export function buildUnitPool(province: Province, race: string): UnitPool {
  const raceStats = RACE_UNIT_STATS[race.toUpperCase()] ?? RACE_UNIT_STATS['HUMAN'];
  const pop = province.population;
  return {
    offenseSpecialists: pop.offenseSpecialistsAtHome ?? pop.offenseSpecialists,
    elites: pop.elitesAtHome ?? pop.elites,
    warHorses: pop.warHorsesAtHome ?? pop.warHorses,
    prisoners: pop.prisonersAtHome ?? pop.prisoners,
    soldiers: pop.soldiersAtHome ?? pop.soldiers,
    mercenaries: 10000,
    offspecOffPoints: raceStats.offense.off,
    eliteOffPoints: raceStats.elite.off,
  };
}

// Bug fix 2 (SF-147 Phase 3 Decision 4): the starting composition — used whenever mwfResult is
// null (no target intel yet) and mode is 'auto' — must be the player's full available army, the
// same values SEND_EVERYTHING already produces, not zero. An all-zero fallback silently defeated
// Component 3's "always-visible Your OP" fix, rendering a correct-looking but wrong 0 instead of
// the player's actual max offense. Shared by buildInitialState (mount) and RESET_FOR_NEW_TARGET
// (target switch) — both need the identical "no target-specific state yet" starting point.
function buildFullArmyDefault(pool: UnitPool): MwfComposition {
  return {
    offenseSpecialists: pool.offenseSpecialists,
    elites: pool.elites,
    warHorses: Math.min(pool.warHorses, pool.offenseSpecialists + pool.elites),
    prisoners: pool.prisoners,
    soldiers: 0,
    mercenaries: 0,
  };
}

function buildInitialState(pool: UnitPool): WarRoomState {
  return seedComposition(
    {
      mode: 'auto',
      generals: 4,
      oversendPct: 1,
      offenseSpecialists: 0,
      elites: 0,
      warHorses: 0,
      prisoners: 0,
      soldiers: 0,
      mercenaries: 0,
      includeSoldiers: false,
      includeMercenaries: false,
      autoSwitchReason: null,
    },
    buildFullArmyDefault(pool)
  );
}

/** Exported for reuse by AmbushLaunchPanel — same base-modifier derivation (ambush's own
 *  restricted defense formula ignores these on the defender side, but the attacker's own offense
 *  modifiers are unaffected and identical to War Room's). */
export function buildAttackMods(province: Province): AttackMods {
  const ome = province.military.offensiveME;

  // SF-180 Decision 5: Warrior's offense modifier is unconditional 1.15 in the engine
  // (MilitarySystem.computeModifiedOffense reads Property.MILITARY_OFFENSE_MOD unconditionally —
  // no war-gating anywhere in the lookup). The old war-gated 1.15/1.05 split had no engine basis on
  // either branch and made the solver mis-send.
  let personalityMod = 1.0;
  const upper = province.metadata.personality.toUpperCase();
  if (upper === 'WARRIOR') {
    personalityMod = 1.15;
  }

  // SF-180 Decision 5: Mage's Fury has zero basis at province-OP level — MagicSystem only modifies
  // Wizard Power per Acre (calculateModifiedOffensiveWPA), never province-level offense. The old
  // 1.20 multiplier here made the MWF solver under-send, causing real combat losses.
  const extraMod = 1.0;

  return { ome, generals: 4, personalityMod, extraMod };
}

/** Small pure display helper shared by both shells' ConfirmAttackDialog instances. */
export function buildSuccessLabel(outcome: Outcome): string {
  switch (outcome) {
    case 'Win':
      return 'Victory expected';
    case 'Lose':
      return 'Defeat expected';
    default:
      return 'Unknown — no SOHD intel';
  }
}

/** Small pure display helper shared by both shells' commit bars. */
export function renderTravelModifiers(t: {
  baseTime: number;
  barracksModifier: number;
  quickFeetModifier: number;
  warModifier: number;
}): string {
  const parts: string[] = [`base ${t.baseTime}d`];
  if (t.barracksModifier < 1) {
    parts.push(`barracks −${Math.round((1 - t.barracksModifier) * 100)}%`);
  }
  if (t.warModifier < 1) {
    parts.push(`war −${Math.round((1 - t.warModifier) * 100)}%`);
  }
  if (t.quickFeetModifier < 1) {
    parts.push(`Quick Feet −${Math.round((1 - t.quickFeetModifier) * 100)}%`);
  }
  return parts.join(' · ');
}

/**
 * Props both MobileWarRoomShell/DesktopWarRoomShell consume — the single object Attack.tsx passes
 * down (Component 1, Phase 3 Decision 7). Named rather than inferred structurally so the contract
 * between useWarRoomState and its two consumers is explicit.
 */
export interface WarRoomShellProps {
  province: Province;
  targetMember: KingdomMember | null;
  intel: TargetIntelData;
  onRunSohd: () => void;

  mode: 'auto' | 'manual';
  onSwitchToAuto: () => void;
  onSwitchToManual: () => void;
  autoSwitchReason: 'impossible' | null;

  generals: number;
  oversendPct: number;
  composition: ArmyComposition;
  includeSoldiers: boolean;
  includeMercenaries: boolean;
  targetOP: number | null;
  onGeneralsChange: (generals: number) => void;
  onOversendChange: (pct: number) => void;
  onFieldChange: (field: keyof ArmyComposition, value: number) => void;
  onToggleSoldiers: (on: boolean) => void;
  onToggleMercenaries: (on: boolean) => void;

  outcome: Outcome;
  attackOP: number;
  targetTD: number | null;
  isStale: boolean;
  isMissingIntel: boolean;
  isImpossible: boolean;
  onSendEverything: () => void;

  // SF-174 Component 6: ingredients for DefenseLeftAtHomeCard/EstimatedGainPanel, both computed
  // client-side by the leaf components themselves (see estimatedGain.ts/homeDefense.ts) — the
  // hook's job is only to wire the raw inputs those pure formulas need through from Attack.tsx's
  // fetched province/kingdom data, not to pre-compute per-Attack-Type results here.
  ownNetworth: number;
  militaryEffectPercentage: number;
  isOwnKingdomAtWar: boolean;
  ownStance: string;
  targetStance: string;
  targetDragonType: string | null;

  travelTime: TravelTimeBreakdown;
  selectedAttackType: AttackType;
  onAttackTypeChange: (type: AttackType) => void;
  isSubmitting: boolean;
  submitError: string | null;
  canLaunch: boolean;
  onLaunchClick: () => void;
  mercenaryTotalCost: number;

  showConfirmDialog: boolean;
  onConfirmAttack: () => void;
  onCancelConfirm: () => void;
  race: string;

  /**
   * SF-183 Component 7: the at-home-only unit pool (see `buildUnitPool`'s contract) — threaded
   * through so `WarRoomTroopControls`/`AmbushLaunchPanel` can cap their own input `max` values
   * against it directly, instead of reading `province.population.*` (away-inclusive) themselves.
   */
  unitPool: UnitPool;
}

export type UseWarRoomStateResult = WarRoomShellProps;

interface UseWarRoomStateProps {
  province: Province;
  intel: TargetIntelData;
  targetMember: KingdomMember | null;
  islandNumber: string;
  kingdomNumber: string;
  selectedAttackType: AttackType;
  onAttackTypeChange: (type: AttackType) => void;
  onRunSohd: () => void;
  worldId: string | null;
  provinceId: string | null;
  refresh: () => Promise<void>;
  onAttackComplete: (result: AttackResult) => void;
  /** Own kingdom's stance/dragon — fetched at page-mount by Attack.tsx's autoLoadOwnKingdom. */
  ownKingdomInfo: KingdomInfo | null;
  /** Target kingdom's stance/dragon — fetched via the "Load Kingdom" flow. */
  targetKingdomInfo: KingdomInfo | null;
}

export function useWarRoomState({
  province,
  intel,
  targetMember,
  islandNumber,
  kingdomNumber,
  selectedAttackType,
  onAttackTypeChange,
  onRunSohd,
  worldId,
  provinceId,
  refresh,
  onAttackComplete,
  ownKingdomInfo,
  targetKingdomInfo,
}: UseWarRoomStateProps): UseWarRoomStateResult {
  const race = province.metadata.race;
  const unitPool = useMemo(() => buildUnitPool(province, race), [province, race]);
  const [state, dispatch] = useReducer(reducer, unitPool, buildInitialState);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  const { sohdReport, sohdData } = intel;

  const attackMods = useMemo((): AttackMods => {
    const base = buildAttackMods(province);
    return { ...base, generals: state.generals };
  }, [province, state.generals]);

  const targetTD = sohdData?.homeDefense ?? null;
  const sohdStale = sohdReport ? isIntelStale(sohdReport.capturedAt) : false;
  const missingIntel = !sohdData;

  const requiredOP = useMemo(
    () => (targetTD !== null ? targetTD * (1 + state.oversendPct / 100) : null),
    [targetTD, state.oversendPct]
  );

  const mwfResult = useMemo(() => {
    if (requiredOP === null) return null;
    return computeMinimumWinningForce(
      requiredOP,
      unitPool,
      state.includeSoldiers,
      state.includeMercenaries,
      state.generals,
      attackMods
    );
  }, [requiredOP, unitPool, state.includeSoldiers, state.includeMercenaries, state.generals, attackMods]);

  const shouldAutoApply = state.mode === 'auto' && mwfResult !== null;

  const effectiveComposition = useMemo((): MwfComposition => {
    if (shouldAutoApply && mwfResult) return mwfResult;
    return {
      offenseSpecialists: state.offenseSpecialists,
      elites: state.elites,
      warHorses: state.warHorses,
      prisoners: state.prisoners,
      soldiers: state.soldiers,
      mercenaries: state.mercenaries,
    };
  }, [shouldAutoApply, mwfResult, state.offenseSpecialists, state.elites, state.warHorses, state.prisoners, state.soldiers, state.mercenaries]);

  const attackOP = useMemo(
    () => computeModifiedOffense(effectiveComposition, unitPool, attackMods),
    [effectiveComposition, unitPool, attackMods]
  );

  const outcome: Outcome = useMemo(() => {
    if (missingIntel || targetTD === null) return 'Unknown';
    return attackOP > targetTD ? 'Win' : 'Lose';
  }, [attackOP, targetTD, missingIntel]);

  const isImpossible = requiredOP !== null && mwfResult === null;

  const travelTime = useMemo(() => {
    const tgtIsland = islandNumber ? parseInt(islandNumber, 10) : undefined;
    const tgtKingdom = kingdomNumber ? parseInt(kingdomNumber, 10) : undefined;
    return calculateTravelTime(province, province.kingdom.isAtWar, tgtIsland, tgtKingdom);
  }, [province, islandNumber, kingdomNumber]);

  const toArmyComposition = useCallback(
    (c: MwfComposition): ArmyComposition => ({
      generals: state.generals,
      soldiers: c.soldiers,
      offenseSpecialists: c.offenseSpecialists,
      elites: c.elites,
      warHorses: c.warHorses,
      prisoners: c.prisoners,
      mercenaries: c.mercenaries,
    }),
    [state.generals]
  );

  const armyForDisplay = toArmyComposition(effectiveComposition);

  const buildFullComposition = useCallback((): MwfComposition => ({
    offenseSpecialists: unitPool.offenseSpecialists,
    elites: unitPool.elites,
    warHorses: Math.min(unitPool.warHorses, unitPool.offenseSpecialists + unitPool.elites),
    prisoners: unitPool.prisoners,
    soldiers: state.includeSoldiers ? unitPool.soldiers : 0,
    mercenaries: state.includeMercenaries ? unitPool.mercenaries : 0,
  }), [unitPool, state.includeSoldiers, state.includeMercenaries]);

  // --- Target-change reset (respecified, Phase 3 — see Component 5's "Target-change reset"
  // note): keyed on the stable primitive targetMember?.provinceId, not object identity. Resets
  // mode/autoSwitchReason via the reducer, and resets the isImpossible edge-detector ref to
  // `false` (below) in the SAME effect, since a reducer can't touch a ref. Declared BEFORE the
  // isImpossible-watching effect so the ref reset always lands before that effect reads it in the
  // same commit.
  const isImpossiblePrevRef = useRef(false);
  const isFirstTargetEffectRunRef = useRef(true);
  const targetProvinceId = targetMember?.provinceId ?? null;
  useEffect(() => {
    // Skip the initial mount firing — buildInitialState already seeded the identical full-army
    // default this effect would otherwise redundantly re-dispatch.
    if (!isFirstTargetEffectRunRef.current) {
      dispatch({ type: 'RESET_FOR_NEW_TARGET', composition: buildFullArmyDefault(unitPool) });
    }
    isFirstTargetEffectRunRef.current = false;
    isImpossiblePrevRef.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetProvinceId]);

  // --- isImpossible auto-switch: fires AUTO_SWITCH_IMPOSSIBLE only on a false→true edge while
  // mode === 'auto' — not on every render where isImpossible stays true, and not at all while
  // already in Manual.
  useEffect(() => {
    const wasImpossible = isImpossiblePrevRef.current;
    isImpossiblePrevRef.current = isImpossible;
    if (!wasImpossible && isImpossible && state.mode === 'auto') {
      dispatch({ type: 'AUTO_SWITCH_IMPOSSIBLE', composition: buildFullComposition() });
    }
  }, [isImpossible, state.mode, buildFullComposition]);

  const handleFieldChange = useCallback(
    (field: keyof ArmyComposition, value: number) => {
      dispatch({ type: 'FIELD_CHANGED', field: field as FieldKey, value });
    },
    []
  );

  const handleSwitchToManual = useCallback(
    () => dispatch({ type: 'SWITCH_TO_MANUAL', seedFrom: mwfResult }),
    [mwfResult]
  );
  const handleSwitchToAuto = useCallback(() => dispatch({ type: 'SWITCH_TO_AUTO' }), []);

  const handleSendEverything = useCallback(() => {
    dispatch({ type: 'SEND_EVERYTHING', composition: buildFullComposition() });
  }, [buildFullComposition]);

  const hasTroops =
    effectiveComposition.offenseSpecialists > 0 ||
    effectiveComposition.elites > 0 ||
    effectiveComposition.prisoners > 0 ||
    effectiveComposition.soldiers > 0 ||
    effectiveComposition.mercenaries > 0;

  const targetProtected = !!targetMember && isProtected(targetMember.protectionExpiresAtTick, province.sync.currentTick);
  const canLaunch = !!targetMember && hasTroops && !isSubmitting && !targetProtected;

  const handleLaunchClick = useCallback(() => {
    setSubmitError(null);

    if (!targetMember) {
      setSubmitError('Select a target province');
      return;
    }

    const validationError = validateArmyComposition({
      generals: state.generals,
      soldiers: armyForDisplay.soldiers,
      includeSoldiers: state.includeSoldiers,
    });
    if (validationError) {
      setSubmitError(validationError);
      return;
    }

    setShowConfirmDialog(true);
  }, [targetMember, state.generals, state.includeSoldiers, armyForDisplay.soldiers]);

  const handleConfirmAttack = useCallback(async () => {
    setShowConfirmDialog(false);

    if (!provinceId || !worldId || !targetMember) {
      setSubmitError('Province ID or World ID not found');
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);
    try {
      const result = await launchAttack(worldId, provinceId, {
        attackType: selectedAttackType,
        targetProvinceId: targetMember.provinceId,
        numberOfGenerals: armyForDisplay.generals,
        numberOfSoldiers: armyForDisplay.soldiers,
        numberOfOffenseSpecialists: armyForDisplay.offenseSpecialists,
        numberOfElites: armyForDisplay.elites,
        numberOfWarHorses: armyForDisplay.warHorses,
        numberOfPrisoners: armyForDisplay.prisoners,
        numberOfMercenaries: armyForDisplay.mercenaries,
      });
      onAttackComplete(result);
      await refresh();
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Failed to launch attack');
    } finally {
      setIsSubmitting(false);
    }
  }, [provinceId, worldId, targetMember, selectedAttackType, armyForDisplay, onAttackComplete, refresh]);

  return {
    province,
    targetMember,
    intel,
    onRunSohd,

    mode: state.mode,
    onSwitchToAuto: handleSwitchToAuto,
    onSwitchToManual: handleSwitchToManual,
    autoSwitchReason: state.autoSwitchReason,

    generals: state.generals,
    oversendPct: state.oversendPct,
    composition: armyForDisplay,
    includeSoldiers: state.includeSoldiers,
    includeMercenaries: state.includeMercenaries,
    targetOP: requiredOP,
    onGeneralsChange: (g: number) => dispatch({ type: 'GENERALS_CHANGED', generals: g }),
    onOversendChange: (pct: number) => dispatch({ type: 'OVERSEND_CHANGED', pct }),
    onFieldChange: handleFieldChange,
    onToggleSoldiers: (on: boolean) => dispatch({ type: 'TOGGLE_INCLUDE_SOLDIERS', on }),
    onToggleMercenaries: (on: boolean) => dispatch({ type: 'TOGGLE_INCLUDE_MERCS', on }),

    outcome,
    attackOP,
    targetTD,
    isStale: sohdStale,
    isMissingIntel: missingIntel,
    isImpossible,
    onSendEverything: handleSendEverything,

    ownNetworth: province.metadata.networth,
    militaryEffectPercentage: province.sciences.progress['MILITARY']?.effectPercentage ?? 0,
    isOwnKingdomAtWar: province.kingdom.isAtWar,
    ownStance: ownKingdomInfo?.stance ?? 'NORMAL',
    targetStance: targetKingdomInfo?.stance ?? 'NORMAL',
    targetDragonType: targetKingdomInfo?.dragon?.dragonType ?? null,

    travelTime,
    selectedAttackType,
    onAttackTypeChange,
    isSubmitting,
    submitError,
    canLaunch,
    onLaunchClick: handleLaunchClick,
    mercenaryTotalCost: effectiveComposition.mercenaries * 25,

    showConfirmDialog,
    onConfirmAttack: handleConfirmAttack,
    onCancelConfirm: () => setShowConfirmDialog(false),
    race,
    unitPool,
  };
}
